"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, PhoneCall, Flame, Droplets, Zap, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, ArrowRight, Sparkles, Layers, Search } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const pipeTypes = [{
  id: "copper",
  name: "Copper Pipes",
  tag: "Common in older & newer homes",
  lifespan: "50+ years",
  commonIssues: "Pin-hole leaks, corrosion from acidic water, solder joint degradation",
  solution: "Spot section replacement, pro-press soldering, or dielectric barrier isolation",
  badgeColor: "default"
}, {
  id: "pex",
  name: "PEX (Cross-linked Polyethylene)",
  tag: "Modern standard",
  lifespan: "40-50 years",
  commonIssues: "Fitting leaks, rodent damage, improper crimping or UV exposure",
  solution: "Quick-crimp coupler rebuild, re-routing through walls without dry-wall damage",
  badgeColor: "secondary"
}, {
  id: "pvc",
  name: "PVC & CPVC",
  tag: "Drain lines & water distribution",
  lifespan: "25-40 years",
  commonIssues: "Brittle joints, cracking under high pressure or freeze-thaw cycles",
  solution: "Solvent weld joint reconstruction or precision PVC sleeve replacements",
  badgeColor: "secondary"
}, {
  id: "castiron",
  name: "Cast Iron & Galvanized",
  tag: "Aging infrastructures",
  lifespan: "30-50 years",
  commonIssues: "Severe internal rust buildup, scale flaking, bottom channel deterioration",
  solution: "Hydro-jet clearing followed by epoxy lining (CIPP) or full trenchless bypass",
  badgeColor: "outline"
}];
const repairMethods = [{
  title: "Trenchless CIPP Pipe Lining",
  category: "Non-Invasive",
  description: "An epoxy-saturated resin sleeve is inserted and cured inside your existing damaged pipe, creating a seamless new interior wall without tearing up floors or landscaping.",
  benefits: "Zero excavation needed, 50-year durability, completed in hours"
}, {
  title: "Sectional Pipe Bursting",
  category: "Full Replacement",
  description: "A hydraulic expansion head fractures the old broken pipe outward while pulling a new continuous high-density polyethylene pipe directly into place.",
  benefits: "Upsizes flow capacity, bypasses roots completely, minimal digging"
}, {
  title: "Precision Spot Repair & Clamping",
  category: "Targeted Fix",
  description: "Ideal for accessible emergency leaks or single-puncture issues. We isolate the damaged sector and install high-grade stainless mechanical couplings or copper sections.",
  benefits: "Immediate pressure restoration, lower cost, rapid 30-minute fix"
}, {
  title: "Epoxy Spray & Hydro-Coating",
  category: "Restoration",
  description: "After high-pressure hydro-jet descaling, micro-fine food-grade polymer barrier coating is applied to seal pinholes and stop recurring internal corrosion.",
  benefits: "Great for vertical risers, seals thousands of micro-fissures"
}];
const faqItems = [{
  question: "How quickly can your Jonesboro technicians arrive for a pipe emergency?",
  answer: "Our mobile plumbing response trucks are stationed locally across Jonesboro, GA. We arrive within 45 to 60 minutes for active water leaks, burst pipes, and sudden flooding."
}, {
  question: "Do you offer trenchless pipe repair for damaged sewer and main lines?",
  answer: "Yes, we prioritize non-invasive trenchless methods such as CIPP epoxy lining and directional bursting to protect your lawn, driveway, and finished flooring."
}, {
  question: "How do you detect leaks hidden behind walls or beneath concrete slabs?",
  answer: "We utilize non-destructive electronic acoustic listening sensors, thermal imaging cameras, and digital fiber-optic pipe inspection cameras to pinpoint exact leak coordinates."
}];
export default function PipeRepair() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedTab, setSelectedTab] = useState("overview");
  return <section id="piperepairoverview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <Wrench className="w-3.5 h-3.5 text-primary" />
              Complete Pipe Diagnostic & Repair
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Comprehensive Pipe Repair & Replacement in Jonesboro
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From sudden slab leaks and corroded copper lines to whole-home repiping and trenchless sewer restoration, we provide dependable 24/7 solutions tailored to your plumbing infrastructure.
          </p>
        </motion.div>

        {/* Top Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <Card className="border border-border/80 shadow-sm bg-card hover:border-primary/40 transition-[border-color,box-shadow]">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Search className="w-6 h-6" />
              </div>
              <CardTitle className="text-lg font-default">Pinpoint Acoustic Leak Detection</CardTitle>
              <CardDescription>
                Zero-damage diagnostic cameras and thermal imaging to isolate underground leaks without exploratory demolition.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/80 shadow-sm bg-card hover:border-primary/40 transition-[border-color,box-shadow]">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Layers className="w-6 h-6" />
              </div>
              <CardTitle className="text-lg font-default">Trenchless Pipe Technology</CardTitle>
              <CardDescription>
                Advanced CIPP lining and hydraulic bursting methods that preserve your driveways, patios, and pristine landscaping.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border border-border/80 shadow-sm bg-card hover:border-primary/40 transition-[border-color,box-shadow]">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <CardTitle className="text-lg font-default">Licensed & Guaranteed Repairs</CardTitle>
              <CardDescription>
                All pipe repairs comply with Georgia plumbing codes and come backed by our comprehensive long-term warranty.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Pipe Material Breakdown and Methods Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-7">
            <div className="mb-6">
              <h3 className="text-2xl font-bold font-default text-foreground mb-2">
                Pipe Materials We Diagnose & Service
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                Every piping material has unique vulnerabilities. Our technicians inspect and service all standard residential and commercial pipe types.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pipeTypes.map((pipe, i) => <div key={pipe.id} data-index={i} className="rounded-xl border border-border bg-card p-5 shadow-xs flex flex-col justify-between hover:shadow-md transition-[border-color,box-shadow]">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-semibold text-foreground text-base">{pipe.name}</span>
                      <Badge variant="outline" className="text-[10px] px-2 py-0">
                        {pipe.lifespan}
                      </Badge>
                    </div>
                    <p className="text-xs text-primary font-medium mb-3">{pipe.tag}</p>
                    <div className="space-y-2 text-xs text-muted-foreground">
                      <p>
                        <strong className="text-foreground">Common Issues:</strong> {pipe.commonIssues}
                      </p>
                      <p>
                        <strong className="text-foreground">Repair Approach:</strong> {pipe.solution}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Emergency Pipe Assistance Banner Card */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-primary/20 bg-primary p-6 sm:p-8 text-primary-foreground shadow-lg flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                  <Flame className="w-3.5 h-3.5" />
                  Immediate Dispatch
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight mb-3">
                  Have a Burst or Leaking Pipe Right Now?
                </h3>
                <p className="text-primary-foreground/90 text-sm leading-relaxed mb-6">
                  Water damage multiplies every minute a pressurized line is broken. Shut off your main water valve and call our 24/7 Jonesboro emergency plumbers right away.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>Average 45-minute on-site arrival</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>Fully-stocked emergency vans</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>Direct insurance claim assistance</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold w-full">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                    <PhoneCall className="w-4 h-4" />
                    Call (470) 847-9076
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-primary text-primary-foreground hover:bg-primary-foreground/10 w-full">
                  <Link to="/contact/" className="flex items-center justify-center gap-2">
                    Request Online
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Repair Methods Tabs */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-xs mb-16">
          <div className="max-w-2xl mb-6">
            <h3 className="text-2xl font-bold font-default text-foreground mb-2">
              Modern Pipe Repair Methodologies
            </h3>
            <p className="text-muted-foreground text-sm">
              We employ the right technique for your home setup to maximize longevity and minimize property disruption.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {repairMethods.map((method, i) => <div key={method.title} data-index={i} className="p-5 rounded-xl border border-border/70 bg-background flex flex-col justify-between hover:border-primary/30 transition-[border-color]">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-foreground text-base">{method.title}</h4>
                    <Badge variant="secondary" className="text-xs">
                      {method.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {method.description}
                  </p>
                </div>
                <div className="pt-3 border-t border-border text-xs flex items-center gap-2 text-foreground font-medium">
                  <Sparkles className="w-3.5 h-3.5 text-accent shrink-0" />
                  <span>{method.benefits}</span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Frequently Asked Questions */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
              Frequently Asked Questions About Pipe Repairs
            </h3>
            <p className="text-muted-foreground text-sm mt-2">
              Clear answers to common questions about pipe replacement, diagnosis, and emergency response.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqItems.map((faq, i) => <AccordionItem key={faq.question} data-index={i} value={`item-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Bottom CTA bar */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h4 className="text-lg font-bold font-default text-foreground">
              Need a quick pipe inspection or emergency estimate?
            </h4>
            <p className="text-sm text-muted-foreground">
              Jonesboro Emergency Plumber is ready 24 hours a day, 7 days a week.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/services/">All Plumbing Services</Link>
            </Button>
            <Button asChild variant="default">
              <Link to="/contact/">Schedule Service</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}